import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import { getClientTypeLabel, resolvePriceClientType } from "../utils/clientType";
import {
  requestPriceListPdfJob,
  getPriceListPdfJobStatus,
  downloadPriceListPdfJob,
  getProducts,
  markPriceAsAvailable,
  markPriceAsSold,
  updateVarietyPrice,
} from "../api";
import CategoryFilterBar from "../components/CategoryFilterBar";
import { normalizePaginatedResponse } from "../utils/pagination";
import InlineLoading from "../components/InlineLoading";
import useProductEvents from '../hooks/useProductEvents';
import { applyProductEvent } from '../utils/productEventPatch';
import usePurchaseEvents from '../hooks/usePurchaseEvents';

const FALLBACK_IMAGE = "https://placehold.co/60";
const clientTypeOrder = {
  detalle: 1,
  mayorista: 2,
  importador: 3,
};

const resolveClientTypeOrder = (value) => clientTypeOrder[value] || 99;

const normalizePriceValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const normalizeName = (value) => String(value || '').trim();
const stripVarietyPrefix = (productName, varietyName) => {
  const productLabel = normalizeName(productName);
  const varietyLabel = normalizeName(varietyName);
  if (!productLabel || !varietyLabel) return varietyLabel;
  const productLower = productLabel.toLowerCase();
  const varietyLower = varietyLabel.toLowerCase();
  if (!varietyLower.startsWith(productLower)) return varietyLabel;
  let stripped = varietyLabel.slice(productLabel.length).trim();
  stripped = stripped.replace(/^[-–—:|/]+\\s*/, '');
  return stripped || varietyLabel;
};

const normalizeCategoryKey = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

const CLIENT_TYPE_FILTERS = [
  { id: "all", label: "Todos" },
  { id: "mayorista", label: "Mayorista" },
  { id: "detalle", label: "Detalle" },
  { id: "importador", label: "Importacion" },
];

const formatPriceLabel = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return `$${parsed.toLocaleString("de-DE")}`;
};

const buildRowLabel = (product, variety, clientTypeLabel) => {
  const strippedVariety = stripVarietyPrefix(product?.productName, variety?.varietyName);
  return [product?.productName, strippedVariety, clientTypeLabel].filter(Boolean).join(" · ");
};


const AdminPriceListPage = ({ embedded = false, onOpenFullPage }) => {
  const { branchId: branchIdParam } = useParams();
  const branchId = branchIdParam || localStorage.getItem("selectedBranchId");
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const canReadProducts = hasPermission(user, "product.read");
  const canUpdateProducts = hasPermission(user, "product.update");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draftPrices, setDraftPrices] = useState({});
  const [savingPrices, setSavingPrices] = useState({});
  const [savingSoldOut, setSavingSoldOut] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedClientType, setSelectedClientType] = useState("all");
  const [activeSaveCount, setActiveSaveCount] = useState(0);
  const [showSavedToast, setShowSavedToast] = useState(false);
  const saveCountRef = useRef(0);
  const savedToastTimer = useRef(null);
  const pendingPdfJobRef = useRef(null);
  const pdfPollTimerRef = useRef(null);
  const pdfPollAttemptsRef = useRef(0);

  const clearSavedToastTimer = () => {
    if (savedToastTimer.current) {
      clearTimeout(savedToastTimer.current);
      savedToastTimer.current = null;
    }
  };

  const triggerSavedToast = () => {
    clearSavedToastTimer();
    setShowSavedToast(true);
    savedToastTimer.current = setTimeout(() => {
      setShowSavedToast(false);
    }, 2500);
  };

  useEffect(() => {
    return () => {
      clearSavedToastTimer();
      if (pdfPollTimerRef.current) {
        clearInterval(pdfPollTimerRef.current);
        pdfPollTimerRef.current = null;
      }
    };
  }, []);

  const clearPendingPdfJob = useCallback(() => {
    pendingPdfJobRef.current = null;
    if (pdfPollTimerRef.current) {
      clearInterval(pdfPollTimerRef.current);
      pdfPollTimerRef.current = null;
    }
    pdfPollAttemptsRef.current = 0;
  }, []);

  const downloadPdfByJob = useCallback(async (jobId) => {
    if (!jobId || !branchId) return;
    try {
      const blob = await downloadPriceListPdfJob(branchId, jobId);
      const fileName = `lista_precios_${branchId || "sucursal"}.pdf`;
      const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading PDF:", err);
      setError("No se pudo generar el PDF.");
    } finally {
      setPdfLoading(false);
      clearPendingPdfJob();
    }
  }, [branchId, clearPendingPdfJob]);

  const startPdfPolling = useCallback((jobId) => {
    if (!jobId || !branchId) return;
    if (pdfPollTimerRef.current) {
      clearInterval(pdfPollTimerRef.current);
    }
    pdfPollAttemptsRef.current = 0;
    pdfPollTimerRef.current = setInterval(async () => {
      if (!pendingPdfJobRef.current || pendingPdfJobRef.current !== jobId) {
        clearPendingPdfJob();
        return;
      }
      pdfPollAttemptsRef.current += 1;
      try {
        const status = await getPriceListPdfJobStatus(branchId, jobId);
        if (status?.status === "ready") {
          await downloadPdfByJob(jobId);
          return;
        }
        if (status?.status === "error") {
          setError("No se pudo generar el PDF.");
          setPdfLoading(false);
          clearPendingPdfJob();
          return;
        }
      } catch (err) {
        console.error("Error polling PDF job:", err);
      }
      if (pdfPollAttemptsRef.current >= 30) {
        setError("El PDF está tardando demasiado. Intenta nuevamente.");
        setPdfLoading(false);
        clearPendingPdfJob();
      }
    }, 4000);
  }, [branchId, clearPendingPdfJob, downloadPdfByJob]);

  const fetchProducts = useCallback(async () => {
    if (!branchId || !canReadProducts) return;
    setLoading(true);
    setError("");
    try {
      const data = await getProducts(branchId, { pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      setProducts(normalized.data || []);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("No se pudieron cargar los productos.");
    } finally {
      setLoading(false);
    }
  }, [branchId, canReadProducts]);

  useEffect(() => {
    if (!canReadProducts) {
      setLoading(false);
      setError("No tienes permisos para ver esta seccion.");
      return;
    }
    if (!branchId) {
      setLoading(false);
      setError("Selecciona una sucursal para ver la lista de precios.");
      return;
    }
    fetchProducts();
  }, [branchId, canReadProducts, fetchProducts]);

  const handleProductEvent = useCallback((event) => {
    const nextProducts = applyProductEvent(products, event);
    if (!nextProducts) {
      fetchProducts();
      return;
    }
    if (nextProducts === products) return;
    setProducts(nextProducts);
  }, [products, fetchProducts]);

  useProductEvents(branchId, handleProductEvent);

  const handlePdfEvent = useCallback((data) => {
    if (!data || !pendingPdfJobRef.current) return;
    if (data.type === "price-list-pdf-ready" && data.jobId === pendingPdfJobRef.current) {
      downloadPdfByJob(data.jobId);
      return;
    }
    if (data.type === "price-list-pdf-error" && data.jobId === pendingPdfJobRef.current) {
      setError("No se pudo generar el PDF.");
      setPdfLoading(false);
      clearPendingPdfJob();
    }
  }, [clearPendingPdfJob, downloadPdfByJob]);

  usePurchaseEvents(branchId, handlePdfEvent, { enabled: !!branchId });

  const productSoldOutMap = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const prices = (product.Varieties || []).flatMap((variety) => variety.VarietyPrices || []);
      const soldOut = prices.length > 0 && prices.every((price) => price.isSoldOut);
      map.set(product.id, soldOut);
    });
    return map;
  }, [products]);

  const rows = useMemo(() => {
    const items = [];
    products.forEach((product) => {
      const productSoldOut = productSoldOutMap.get(product.id);
      (product.Varieties || []).forEach((variety) => {
        (variety.VarietyPrices || []).forEach((price) => {
          const clientType = resolvePriceClientType(price);
          items.push({
            key: price.id,
            product,
            variety,
            price,
            clientType,
            clientTypeLabel: getClientTypeLabel(clientType),
            productSoldOut,
            categoryId: normalizeCategoryKey(product?.categoryId ?? product?.Category?.id ?? product?.category),
          });
        });
      });
    });
    return items.sort((a, b) => {
      const productCompare = a.product.productName.localeCompare(b.product.productName);
      if (productCompare !== 0) return productCompare;
      const varietyCompare = a.variety.varietyName.localeCompare(b.variety.varietyName);
      if (varietyCompare !== 0) return varietyCompare;
      return resolveClientTypeOrder(a.clientType) - resolveClientTypeOrder(b.clientType);
    });
  }, [products, productSoldOutMap]);

  const categoryOptions = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const categoryId = product?.categoryId ?? product?.Category?.id ?? product?.category;
      const categoryLabel =
        product?.Category?.name ||
        product?.categoryName ||
        product?.categoryLabel ||
        (typeof product?.category === "string" ? product.category : null) ||
        (categoryId !== undefined && categoryId !== null ? `Categoria ${categoryId}` : null);
      const resolvedId = normalizeCategoryKey(categoryId ?? categoryLabel);
      const resolvedLabel = normalizeCategoryKey(categoryLabel) || resolvedId;
      if (!resolvedId || !resolvedLabel) return;
      if (!map.has(resolvedId)) {
        map.set(resolvedId, {
          id: resolvedId,
          label: resolvedLabel,
          initial: String(resolvedLabel).trim().charAt(0).toUpperCase(),
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [products]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const categoryFilter = selectedCategory !== "all" ? String(selectedCategory) : null;
    const clientTypeFilter = selectedClientType !== "all" ? String(selectedClientType) : null;
    return rows.filter((row) => {
      if (categoryFilter && String(row.categoryId) !== categoryFilter) return false;
      if (clientTypeFilter && row.clientType !== clientTypeFilter) return false;
      if (normalizedSearch) {
        const productName = String(row.product?.productName || "").toLowerCase();
        const varietyName = String(row.variety?.varietyName || "").toLowerCase();
        if (!productName.includes(normalizedSearch) && !varietyName.includes(normalizedSearch)) {
          return false;
        }
      }
      return true;
    });
  }, [rows, searchTerm, selectedCategory, selectedClientType]);

  const updatePriceValue = (priceId, priceValue) => {
    setProducts((prev) =>
      prev.map((product) => ({
        ...product,
        Varieties: (product.Varieties || []).map((variety) => ({
          ...variety,
          VarietyPrices: (variety.VarietyPrices || []).map((price) =>
            price.id === priceId ? { ...price, price: priceValue } : price
          ),
        })),
      }))
    );
  };

  const updatePriceSoldOut = (priceId, nextSoldOut) => {
    setProducts((prev) =>
      prev.map((product) => ({
        ...product,
        Varieties: (product.Varieties || []).map((variety) => ({
          ...variety,
          VarietyPrices: (variety.VarietyPrices || []).map((price) =>
            price.id === priceId ? { ...price, isSoldOut: nextSoldOut } : price
          ),
        })),
      }))
    );
  };

  const handlePriceChange = (priceId, value) => {
    setDraftPrices((prev) => ({
      ...prev,
      [priceId]: value,
    }));
  };

  const handleSavePrice = async (price) => {
    if (!canUpdateProducts || !branchId) return;
    const draft = draftPrices[price.id];
    const resolved = normalizePriceValue(draft);
    if (resolved === "") {
      setError("El precio es obligatorio.");
      return;
    }
    const parsed = Number(resolved);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError("El precio debe ser un numero valido.");
      return;
    }
    clearSavedToastTimer();
    setShowSavedToast(false);
    saveCountRef.current += 1;
    setActiveSaveCount(saveCountRef.current);
    setSavingPrices((prev) => ({ ...prev, [price.id]: true }));
    setError("");
    try {
      await updateVarietyPrice(branchId, price.id, { price: parsed });
      updatePriceValue(price.id, parsed);
      setDraftPrices((prev) => {
        const next = { ...prev };
        delete next[price.id];
        return next;
      });
    } catch (err) {
      console.error("Error updating price:", err);
      setError("No se pudo actualizar el precio.");
    } finally {
      setSavingPrices((prev) => ({ ...prev, [price.id]: false }));
      saveCountRef.current = Math.max(saveCountRef.current - 1, 0);
      setActiveSaveCount(saveCountRef.current);
      if (saveCountRef.current === 0) {
        triggerSavedToast();
      }
    }
  };

  const handleToggleSoldOut = async (price) => {
    if (!canUpdateProducts || !branchId) return;
    const nextSoldOut = !price.isSoldOut;
    setSavingSoldOut((prev) => ({ ...prev, [price.id]: true }));
    setError("");
    try {
      if (nextSoldOut) {
        await markPriceAsSold(branchId, price.id);
      } else {
        await markPriceAsAvailable(branchId, price.id);
      }
      updatePriceSoldOut(price.id, nextSoldOut);
    } catch (err) {
      console.error("Error updating sold out state:", err);
      setError("No se pudo actualizar el estado de agotado.");
    } finally {
      setSavingSoldOut((prev) => ({ ...prev, [price.id]: false }));
    }
  };

  const handleDownloadPdf = async () => {
    if (!canReadProducts) return;
    if (!filteredRows.length) {
      setError("No hay resultados para exportar.");
      return;
    }
    setPdfLoading(true);
    try {
      setError("");
      const trimmedSearch = searchTerm.trim();
      clearPendingPdfJob();
      const result = await requestPriceListPdfJob(branchId, {
        clientType: selectedClientType,
        category: selectedCategory,
        search: trimmedSearch || undefined,
        includeImages: 1,
      });
      const jobId = result?.jobId;
      if (!jobId) {
        throw new Error("Missing PDF job id");
      }
      if (result?.status === "ready") {
        await downloadPdfByJob(jobId);
        return;
      }
      pendingPdfJobRef.current = jobId;
      startPdfPolling(jobId);
    } catch (err) {
      console.error("Error generating PDF:", err);
      setError("No se pudo generar el PDF.");
      setPdfLoading(false);
      clearPendingPdfJob();
    }
  };

  const renderRows = (withActions) =>
    filteredRows.map((row, index) => {
      const { product, variety, price, clientTypeLabel, productSoldOut } = row;
      const imageSrc =
        price.priceImage ||
        variety.varietyImage ||
        product.productImage ||
        FALLBACK_IMAGE;
      const rowIsSoldOut = price.isSoldOut;
      const rowOpacity = rowIsSoldOut ? "opacity-70" : "";
      const zebraTone = index % 2 === 0 ? "bg-white" : "bg-slate-50";
      const rowTone = rowIsSoldOut ? "bg-amber-50" : zebraTone;
      const draftValue =
        draftPrices[price.id] !== undefined
          ? draftPrices[price.id]
          : normalizePriceValue(price.price);
      const isDirty = normalizePriceValue(price.price) !== normalizePriceValue(draftValue);
      const isSaving = savingPrices[price.id];
      const isToggling = savingSoldOut[price.id];
      const combinedLabel = buildRowLabel(product, variety, clientTypeLabel);
      const varietyLabel = stripVarietyPrefix(product?.productName, variety?.varietyName);

      return (
        <tr
          key={price.id}
          className={`border-b last:border-b-0 ${rowTone}`}
        >
          <td className={`px-2 py-2 lg:hidden ${rowOpacity}`}>
            <div className="flex items-start gap-2">
              <img
                src={imageSrc}
                alt={product.productName}
                className="h-10 w-10 rounded object-cover border border-gray-200 flex-none"
              />
              <div className="min-w-0">
                <div className="font-semibold text-sm leading-tight break-words">{combinedLabel}</div>
                {productSoldOut && (
                  <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 mt-1">
                    Producto agotado
                  </span>
                )}
              </div>
            </div>
          </td>
          <td className={`hidden lg:table-cell px-2 py-2 ${rowOpacity}`}>
            <div className="flex items-center gap-2">
              <img
                src={imageSrc}
                alt={product.productName}
                className="h-10 w-10 rounded object-cover border border-gray-200 flex-none"
              />
              <div className="min-w-0">
                <div className="font-semibold text-sm leading-tight break-words">{product?.productName || "-"}</div>
                {productSoldOut && (
                  <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 mt-1">
                    Producto agotado
                  </span>
                )}
              </div>
            </div>
          </td>
          <td className={`hidden lg:table-cell px-2 py-2 text-sm ${rowOpacity}`}>
            <span className="break-words">{varietyLabel || "-"}</span>
          </td>
          <td className={`hidden lg:table-cell px-2 py-2 text-sm ${rowOpacity}`}>{clientTypeLabel || "-"}</td>
          <td className={`px-2 py-2 text-sm ${rowOpacity}`}>{price.metricUnit || "-"}</td>
          <td className={`px-2 py-2 text-sm ${rowOpacity}`}>
            {withActions && canUpdateProducts ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  className="w-20 sm:w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                  value={draftValue}
                  onChange={(event) => handlePriceChange(price.id, event.target.value)}
                  min="0"
                  onBlur={() => {
                    if (!isDirty || isSaving) return;
                    handleSavePrice(price);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    event.preventDefault();
                    if (!isDirty || isSaving) return;
                    handleSavePrice(price);
                  }}
                />
              </div>
            ) : (
              <span>{formatPriceLabel(price.price)}</span>
            )}
          </td>
          <td className={`px-2 py-2 text-sm ${rowOpacity}`}>
            {withActions && canUpdateProducts ? (
              <button
                type="button"
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                  rowIsSoldOut
                    ? "bg-red-100 text-red-700 border-red-200"
                    : "bg-emerald-100 text-emerald-700 border-emerald-200"
                }`}
                disabled={isToggling}
                onClick={() => handleToggleSoldOut(price)}
              >
                {isToggling ? "Actualizando..." : rowIsSoldOut ? "Agotado" : "Disponible"}
              </button>
            ) : (
              <span className={rowIsSoldOut ? "text-red-700" : "text-emerald-700"}>
                {rowIsSoldOut ? "Agotado" : "Disponible"}
              </span>
            )}
          </td>
        </tr>
      );
    });

  if (loading) {
    return (
      <div className="p-6 text-center text-sm text-gray-600">
        <InlineLoading label="Cargando lista de precios" />
      </div>
    );
  }

  if (error && !rows.length) {
    return <div className="p-6 text-center text-sm text-gray-600">{error}</div>;
  }

  if (!rows.length) {
    return <div className="p-6 text-center text-sm text-gray-600">No hay precios registrados.</div>;
  }

  const handleBackToSettings = () => {
    navigate("/settings?topic=catalog&section=catalog-price-list");
  };

  return (
    <div className={embedded ? "space-y-4" : "p-6 bg-gray-100 min-h-screen"}>
      <div className={embedded ? "space-y-4" : "max-w-6xl mx-auto space-y-4"}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className={embedded ? "text-xl font-bold" : "text-2xl font-bold"}>
              Lista de precios
            </h1>
            <p className="text-sm text-gray-600">
              Actualiza precios y disponibilidad por variedad desde una sola tabla.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!embedded && (
              <button
                type="button"
                onClick={handleBackToSettings}
                className="px-3 py-2 rounded border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-100"
              >
                Volver a configuracion
              </button>
            )}
            {embedded && (
              <button
                type="button"
                onClick={onOpenFullPage || (() => navigate(`/branches/${branchId}/price-list`))}
                className="px-3 py-2 rounded border border-blue-200 text-sm font-semibold text-blue-700 hover:bg-blue-50"
              >
                Ver pantalla completa
              </button>
            )}
            <button
              type="button"
              onClick={handleDownloadPdf}
              className="px-4 py-2 rounded bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-300"
              disabled={pdfLoading}
            >
              {pdfLoading ? <InlineLoading label="Generando PDF" /> : "Descargar PDF"}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Buscar producto o variedad..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="flex flex-wrap items-center gap-2">
            {CLIENT_TYPE_FILTERS.map((option) => {
              const isSelected = selectedClientType === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSelectedClientType(option.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${
                    isSelected
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <CategoryFilterBar
            categories={categoryOptions}
            selectedCategory={selectedCategory}
            onSelect={setSelectedCategory}
            includeAll
            allItem={{ id: "all", label: "Todas", initial: "T" }}
            itemsWrapperClassName="pb-2"
            emptyMessage="No hay categorias disponibles."
            isEmpty={categoryOptions.length === 0}
          />
        </div>

        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-left text-sm" id="price-list-table">
            <thead className="bg-gray-100 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-2 py-2 lg:hidden">Producto / Variedad / Tipo</th>
                <th className="hidden lg:table-cell px-2 py-2">Producto</th>
                <th className="hidden lg:table-cell px-2 py-2">Variedad</th>
                <th className="hidden lg:table-cell px-2 py-2">Tipo</th>
                <th className="px-2 py-2">Unidad</th>
                <th className="px-2 py-2">Precio</th>
                <th className="px-2 py-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length ? (
                renderRows(true)
              ) : (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-gray-500">
                    No hay resultados para los filtros seleccionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(activeSaveCount > 0 || showSavedToast) && (
        <div className="fixed bottom-4 right-4 z-50" aria-live="polite">
          <div
            className={`rounded-xl shadow-lg px-4 py-3 text-sm text-white ${
              activeSaveCount > 0 ? "bg-slate-800" : "bg-emerald-600"
            }`}
          >
            {activeSaveCount > 0 ? "Guardando..." : "Guardado"}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPriceListPage;
